import { Decimal } from 'decimal.js'
import { EDietComponentType } from '../constants/components'

const BASE_WEIGHT = 100
const BASE_THERMAL_PROCESSING_COEFFICIENT = 100

/**
 * Transforms the snake_case JSON object into a camelCase model object for a Diet Semi Product Component Pivot.
 *
 * @param {IDietSemiProductComponentPivot} json - The JSON object containing diet semi product component data in snake_case format
 * @returns {IDietSemiProductComponentPivotModel} A transformed object with camelCase properties and numeric weight values
 */
export const DietSemiProductComponentPivot = (
	json: IDietSemiProductComponentPivot,
): IDietSemiProductComponentPivotModel => {
	const { component_type, net_weight, raw_weight, weight_after_processing, weight_unit, ...rest } = json

	return {
		...rest,
		componentType: component_type,
		netWeight: Number.isNaN(Number(net_weight)) ? 0 : Number(net_weight),
		rawWeight: Number.isNaN(Number(raw_weight)) ? 0 : Number(raw_weight),
		weightAfterProcessing: Number.isNaN(Number(weight_after_processing)) ? 0 : Number(weight_after_processing),
		weightUnit: weight_unit,
	}
}

/**
 * Transforms a raw diet semi-product component JSON into a structured model.
 *
 * @param {IDietSemiProductComponent} json - The raw JSON data of the diet semi-product component
 * @returns {IDietSemiProductComponentModel} A structured model of the diet semi-product component
 */
export const DietSemiProductComponent = (json: IDietSemiProductComponent): IDietSemiProductComponentModel => {
	const {
		name,
		internal_name,
		warehouse_name,
		pivot,
		food_category,
		is_active,
		nutritional_parameters,
		thermal_processing_coefficient,
		mechanical_processing_coefficient,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		foodCategory: food_category,
		pivot: DietSemiProductComponentPivot(pivot),
		nutritionalParameters: nutritional_parameters
			? nutritional_parameters.map(n => NutritionalParameterWithValue(n))
			: [],
		thermalProcessingCoefficient: thermal_processing_coefficient,
		mechanicalProcessingCoefficient: mechanical_processing_coefficient,
	}
}

/**
 * Transforms a diet semi-product JSON object into a model object.
 *
 * @param {IDietSemiProduct} json
 */
export const DietSemiProduct = (json: IDietSemiProduct): IDietSemiProductModel => {
	const {
		name,
		standardization_factor,
		internal_name,
		warehouse_name,
		is_active,
		nutritional_parameters,
		allergens,
		components,
		created_at,
		updated_at,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		standardizationFactor: standardization_factor,
		nutritionalParameters: nutritional_parameters.map(n => NutritionalParameter(n)),
		allergens: allergens.map(a => DietAllergen(a)),
		components: components.map(c => DietSemiProductComponent(c)),
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

/**
 * Transforms a diet semi-product details JSON object into a model object.
 *
 * @param {IDietSemiProductDetails} json - The JSON object containing diet semi-product details
 * @returns {IDietSemiProductDetailsModel} A formatted diet semi-product details model with transformed properties
 */
export const DietSemiProductDetails = (json: IDietSemiProductDetails): IDietSemiProductDetailsModel => {
	const { dietary_tags, ...rest } = json

	const general = DietSemiProduct(json)

	return {
		...rest,
		...general,
		dietaryTags: dietary_tags.map(d => DietDietaryTag(d)),
	}
}

/**
 * History
 */
// TODO: Preare for a model - history is not ready yet
export const DietSemiProductHistory = (json: IDietSemiProductHistory): IDietSemiProductHistoryModel => {
	const { date, ...rest } = json

	return {
		...rest,
		date: date ? new Date(date) : null,
	}
}

/**
 * Forms and inside models
 */

/**
 * Converts a diet semi-product component model to a form model.
 *
 * @param {IDietSemiProductComponentModel} model - The source diet semi-product component model
 * @returns {IDietSemiProductComponentFormModel} The converted form model with processed weight values
 */
export const DietSemiProductComponentToFormModel = (
	model: IDietSemiProductComponentModel,
): IDietSemiProductComponentFormModel => {
	const { pivot, type, thermalProcessingCoefficient, nutritionalParameters, ...rest } = model

	const { group, rawWeight, netWeight, weightUnit, weightAfterProcessing } = pivot

	const difference =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2)) : 100

	const _thermalProcessingCoefficient =
		type === EDietComponentType.MATERIAL ? thermalProcessingCoefficient : difference

	return {
		...rest,
		group,
		type,
		weightUnit,
		nutritionalParameters,
		rawWeight: Number.isNaN(Number(rawWeight)) ? BASE_WEIGHT : Number(rawWeight),
		netWeight: Number.isNaN(Number(netWeight)) ? BASE_WEIGHT : Number(netWeight),
		thermalProcessingCoefficient: _thermalProcessingCoefficient ?? BASE_THERMAL_PROCESSING_COEFFICIENT,
		weightAfterProcessing: Number.isNaN(Number(weightAfterProcessing))
			? BASE_WEIGHT
			: Number(weightAfterProcessing),
	}
}

/**
 * Converts a raw material model to a semi-product component form model.
 *
 * @param {IDietComponentsRawMaterialModel} model - The raw material model to convert
 * @returns {IDietSemiProductComponentFormModel} A semi-product component form model with default weights of 100 and
 * type set to MATERIAL
 */
export const DietSemiProductSearchComponentRawMaterialToFormModel = (
	model: IDietComponentsRawMaterialModel,
): IDietSemiProductComponentFormModel => {
	const { unit, ...rest } = model

	return {
		...rest,
		group: null,
		weightUnit: unit,
		netWeight: BASE_WEIGHT,
		rawWeight: BASE_WEIGHT,
		weightAfterProcessing: BASE_WEIGHT,
		thermalProcessingCoefficient: BASE_THERMAL_PROCESSING_COEFFICIENT,
		type: EDietComponentType.MATERIAL,
	}
}

/**
 * Converts a diet material model to a diet semi-product component form model.
 *
 * @param {IDietMaterialModel} model - The diet material model to convert
 * @returns {IDietSemiProductComponentFormModel} A form model for a diet semi-product component
 */
export const DietSemiProductSearchComponentMaterialToFormModel = (
	model: IDietMaterialModel,
): IDietSemiProductComponentFormModel => {
	const { unit, thermalProcessingCoefficient, ...rest } = model

	return {
		...rest,
		group: null,
		weightUnit: unit,
		netWeight: BASE_WEIGHT,
		rawWeight: BASE_WEIGHT,
		weightAfterProcessing: BASE_WEIGHT,
		thermalProcessingCoefficient: thermalProcessingCoefficient ?? BASE_THERMAL_PROCESSING_COEFFICIENT,
		type: EDietComponentType.MATERIAL,
	}
}

function DietSemiProductSearchComponentSemiProductComponentToFormModel(
	model: IDietComponentsSemiProductComponentModel,
): IDietSemiProductComponentFormModel {
	const { pivot, ...rest } = model

	const { weightUnit, rawWeight, netWeight, weightAfterProcessing } = pivot

	const thermalProcessingCoefficient =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2)) : 100

	return {
		...rest,
		group: null,
		weightUnit,
		thermalProcessingCoefficient: thermalProcessingCoefficient ?? BASE_THERMAL_PROCESSING_COEFFICIENT,
		rawWeight: Number.isNaN(Number(rawWeight)) ? BASE_WEIGHT : Number(rawWeight),
		netWeight: Number.isNaN(Number(netWeight)) ? BASE_WEIGHT : Number(netWeight),
		weightAfterProcessing: Number.isNaN(Number(weightAfterProcessing))
			? BASE_WEIGHT
			: Number(weightAfterProcessing),
	}
}

/**
 * Converts a Diet Component Semi Product Model to a Diet Semi Product Component Form Model.
 *
 * @param {IDietComponentsSemiProductModel} model - The source semi product model to convert
 * @returns {IDietSemiProductComponentFormModel} The converted form model with calculated properties
 */
export const DietSemiProductSearchComponentSemiProductToFormModel = (
	model: IDietComponentsSemiProductModel,
): IDietSemiProductComponentFormModel => {
	const { components, ...rest } = model

	// TODO: Change the way of getting the weight unit
	const weightUnit = components[0]?.pivot.weightUnit

	const netWeight = components.reduce((acc, curr) => acc + Number(curr.pivot.netWeight), 0)
	const rawWeight = components.reduce((acc, curr) => acc + Number(curr.pivot.rawWeight), 0)
	const weightAfterProcessing = components.reduce((acc, curr) => acc + Number(curr.pivot.weightAfterProcessing), 0)

	const thermalProcessingCoefficient =
		netWeight > 0 ? Number(new Decimal(weightAfterProcessing).div(netWeight).times(100).toFixed(2)) : 100

	return {
		...rest,
		group: null,
		type: EDietComponentType.SEMIPRODUCT,
		weightUnit,
		rawWeight: Number.isNaN(rawWeight) ? BASE_WEIGHT : rawWeight,
		netWeight: Number.isNaN(netWeight) ? BASE_WEIGHT : netWeight,
		thermalProcessingCoefficient: thermalProcessingCoefficient ?? BASE_THERMAL_PROCESSING_COEFFICIENT,
		weightAfterProcessing: Number.isNaN(weightAfterProcessing) ? BASE_WEIGHT : weightAfterProcessing,
		components: components.map(c => DietSemiProductSearchComponentSemiProductComponentToFormModel(c)),
	}
}

/**
 * Payloads
 */
const DietSemiProductComponentToPayload = (
	component: IDietSemiProductComponentFormModel,
): IDietSemiProductComponentPayload => {
	const { id, type, group, weightUnit, netWeight, rawWeight, weightAfterProcessing } = component

	return {
		id,
		type,
		group,
		weight_unit: weightUnit,
		net_weight: netWeight,
		raw_weight: rawWeight,
		weight_after_processing: weightAfterProcessing,
	}
}

const DietSemiProductSharedToPayload = (
	event: IFormSubmitEvent<IDietSemiProductForm>,
	others: IDietSemiProductPayloadOthers,
): IDietSemiProductPayloadShared => {
	const { name, warehouseName, isWarehouseNameSameAsInternalName } = event.values

	const { allergens, dietaryTags, components, productionSteps } = others

	const _components = components.flatMap(component => {
		if (!component.group) return [DietSemiProductComponentToPayload(component)]

		return component.components?.map(nested => DietSemiProductComponentToPayload(nested)) ?? []
	})

	return {
		warehouse_name: isWarehouseNameSameAsInternalName ? name : warehouseName,
		allergens: allergens ? allergens : [],
		dietary_tags: dietaryTags,
		components: _components,
		// TODO: Fix higher level model while refactoring
		// @ts-expect-error Fix higher level model
		food_production_stages: productionSteps ? productionSteps.map(s => DietProductionStepToPayload(s)) : [],
	}
}

export const DietSemiProductUpdateToPayload = (
	updated: IDietSemiProductDetailsModel,
	event: IFormSubmitEvent<IDietSemiProductForm>,
	others: IDietSemiProductPayloadOthers,
): IDietSemiProductUpdatePayload => {
	const { name } = event.values

	const shared = DietSemiProductSharedToPayload(event, others)

	return {
		...shared,
		id: updated.id,
		name: LangContentToJson(updated.name, name),
		is_active: updated.isActive,
	}
}

export const DietSemiProductCreateToPayload = (
	event: IFormSubmitEvent<IDietSemiProductForm>,
	others: IDietSemiProductPayloadOthers,
): IDietSemiProductCreatePayload => {
	const { name } = event.values

	const shared = DietSemiProductSharedToPayload(event, others)

	return {
		...shared,
		name: generateLangContentArray(name),
		is_active: true,
	}
}

// TODO: Remove this and heigher level models
/**
 * Old methods for backward compatibility
 */
export const dietSemiProductComponentToJSON = (
	model: IDietSemiProductComponentFormModel,
): IDietSemiProductComponentFormJson => {
	// @ts-expect-error model to remove
	const { netWeight, rawWeight, weightAfterProcessing, weightUnit, packagingTypeId, ...rest } = model

	return {
		...rest,
		net_weight: netWeight,
		raw_weight: rawWeight,
		weight_after_processing: weightAfterProcessing,
		weight_unit: weightUnit,
		// @ts-expect-error model to remove
		packaging_type_id: packagingTypeId,
	}
}
